import React, { useEffect, useState } from 'react';
import { ConfigProvider, Table, message, Tag, Tooltip, Spin, Select, Input, Button, Dropdown, Popconfirm } from 'antd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { IconBuildingFactory2, IconCheck, IconCloudRain, IconGrain, IconMessage, IconSnowflake, IconTool, IconX, IconFile } from '@tabler/icons-react';
import { DownOutlined } from '@ant-design/icons';
import moment from "moment";
import { IconTractor, IconUser } from '@tabler/icons-react';
import api from "../../../authentication/axiosInstance";
import { Typography } from '@mui/material';
import styles from '../../allocate/walkway-crew/WalkwayCrewAllocationPage.module.css';

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

const counterBoxStyle = {
    border: '1px solid lightgrey',
    padding: '0px 10px 5px 10px',
    backgroundColor: '#ffffff', // White background for individual boxes
    borderRadius: '6px',
    minWidth: '200px', // Ensures consistent width
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' // Optional: adds a subtle shadow
};

// Styles for the counter titles
const counterTitleStyle = {
    margin: "10px 0px 5px 0px",
    fontSize: '1em',
    color: '#555'
};

const metricCardSubtitle = {
    marginTop: '0px',
    paddingTop: '0px',
    fontSize: '12px',
    fontWeight: '400',
    color: 'gray',
};

const getStaffCardBackgroundColor = (status) => {
    switch (status) {
        case "Confirmed":
            return "#E5F2EC"; // Light Green
        case "Denied":
            return "#FDEAEA"; // Light Red
        default:
            return "#f0f0f0"; // Default Grey
    }
};



const stormSeverityColors = [
    { severity: "light", color: "#034832", bg: "#E5F2EC" },
    { severity: "medium", color: "#FFC107", bg: "#FFF8E1" },
    { severity: "heavy", color: "#FF4D4E", bg: "#FDEAEA" },
    { severity: "N/A", color: "#808080", bg: "#F5F5F5" },
]

const getSeverityColor = (severity) => {
    const severityColor = stormSeverityColors.find(item => item.severity === severity);
    return severityColor ? severityColor : { color: "#000000", bg: "#FFFFFF" }; // Default fallback
};

const generateStaffTimesheet = async (stormStart, stormEnd, staffName, vehicleName) => {
    try {
        // Convert stormStart and stormEnd to Unix timestamps
        const stormStartUnix = dayjs(stormStart).unix();
        const stormEndUnix = dayjs(stormEnd).unix();

        // Display a loading message
        message.loading({ content: "Generating timesheet...", key: "timesheet" });

        // Send the request to the API
        const response = await api.post(
            '/viaesys/generate-staff-timesheet/',
            {
                storm_start: stormStartUnix, // Send as Unix timestamp
                storm_end: stormEndUnix,    // Send as Unix timestamp
                staff_name: staffName,
                vehicle_name: vehicleName,
            },
            { responseType: "blob" } // Ensure response is treated as a file blob
        );

        // Check if the response is successful
        if (response.status === 200) {
            // Create a blob from the response data
            const blob = new Blob([response.data], { type: "text/csv" });

            // Generate a download URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement("a");
            link.href = url;

            // Set the download filename using the staff's name
            link.setAttribute("download", `${staffName}_timesheet.csv`);

            // Append the link to the document, trigger the download, and remove the link
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Display a success message
            message.success({ content: `Timesheet downloaded for ${staffName}!`, key: "timesheet" });
        } else {
            console.error('Failed to generate timesheet:', response.statusText);
            message.error('Failed to generate timesheet. Please try again.');
        }
    } catch (error) {
        console.error('Error generating timesheet:', error);
        message.error('An error occurred while generating the timesheet.');
    }
};

const MessageStatusCounter = ({ counts }) => {
    return (
        <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '10px',
            marginBottom: '10px',
            marginRight: '10px',
            fontFamily: 'Plus Jakarta Sans, sans-serif'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 12px',
                borderRadius: '5px',
                backgroundColor: '#f0f0f0',
                gap: '6px'
            }}>
                <span style={{ color: '#555555', fontSize: '14px', fontWeight: '500' }}>Unread</span>
                <span style={{
                    backgroundColor: '#034832',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: '600'
                }}>{counts.unread || 0}</span>
            </div>

            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 12px',
                borderRadius: '5px',
                backgroundColor: '#E5F2EC',
                gap: '6px'
            }}>
                <span style={{ color: '#034832', fontSize: '14px', fontWeight: '500' }}>Confirmed</span>
                <span style={{
                    backgroundColor: '#034832',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: '600'
                }}>{counts.confirmed || 0}</span>
            </div>

            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 12px',
                borderRadius: '5px',
                backgroundColor: '#FDEAEA',
                gap: '6px'
            }}>
                <span style={{ color: '#FF4D4E', fontSize: '14px', fontWeight: '500' }}>Denied</span>
                <span style={{
                    backgroundColor: '#FF4D4E',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: '600'
                }}>{counts.denied || 0}</span>
            </div>
        </div>
    );
};


const DispatchHistoryPage = () => {
    const [loading, setLoading] = useState(false);

    const [objectFilter, setObjectFilter] = useState('Walkway')
    const [regionFilter, setRegionFilter] = useState([])
    const [vehicleSearch, setVehicleSearch] = useState('');
    const [staffSearch, setStaffSearch] = useState('');
    const [siteSearch, setSiteSearch] = useState('');
    const [stormIdFilter, setStormIdFilter] = useState('All');
    const [stormOptions, setStormOptions] = useState([]);
    const [regionOptions, setRegionOptions] = useState([]);
    const [isStormFilterSet, setIsStormFilterSet] = useState(false);


    const [counter, setCounter] = useState({
        "Walkway": { unread: 0, confirmed: 0, denied: 0 },
        "Salting": { unread: 0, confirmed: 0, denied: 0 },
        "Loader": { unread: 0, confirmed: 0, denied: 0 }
    });

    const handleResendDispatchMessage = async (dispatch_log_id, staff_id, type) => {
        if (type === "loader") {
            const response = await api.post("/dispatch/dispatchLogs/", {
                resend_loader_message: true,
                dispatch_log_id: dispatch_log_id,
                staff_id: staff_id,
            });

            if (response.status === 200) {
                message.success('Message resent successfully!');
            } else {
                message.error('Failed to resend the message.');
            }
        } else if (type === "walkwayCrew") {
            const response = await api.post("/dispatch/dispatchLogs/", {
                resend_walkway_crew_message: true,
                dispatch_log_id: dispatch_log_id,
                staff_id: staff_id,
            });

            if (response.status === 200) {
                message.success('Message resent successfully!');
            } else {
                message.error('Failed to resend the message.');
            }
        } else if (type === "saltingRoute") {
            const response = await api.post("/dispatch/dispatchLogs/", {
                resend_salting_route_message: true,
                dispatch_log_id: dispatch_log_id,
                staff_id: staff_id,
            });

            if (response.status === 200) {
                message.success('Message resent successfully!');
            } else {
                message.error('Failed to resend the message.');
            }
        }
    };

    const walkwayCrewsColumns = [
        {
            title: "Crew Name",
            dataIndex: "name", // Specifies the data field
            key: "name",
            width: 100,
            sorter: (a, b) => {
                const nameA = a.name ? a.name.toLowerCase() : "";
                const nameB = b.name ? b.name.toLowerCase() : "";
                return nameA.localeCompare(nameB);
            },
            sortDirections: ['ascend', 'descend'],
            render: (text) => <b>{text}</b>, // Renders the text in bold
        },
        {
            title: "Region",
            dataIndex: "region", // Use dataIndex for better integration with Ant Design
            key: "region",
            width: 60,
            sorter: (a, b) => {
                // Handle cases where region might be null or undefined
                const regionA = a.region ? a.region.toLowerCase() : "";
                const regionB = b.region ? b.region.toLowerCase() : "";
                if (regionA < regionB) return -1;
                if (regionA > regionB) return 1;
                return 0;
            },
            sortDirections: ['ascend', 'descend'],
            render: (text) => <b>{text}</b>,
        },
        {
            title: "Event",
            key: "storm_type",
            width: 180,
            // render: (record) => <span>{record.storm_type} ({record.storm_severity})</span>,
            render: (text, record) => {
                const { color, bg } = getSeverityColor(record.storm_severity);
                return (
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', width: '100%', height: '50px' }}>
                        {record.storm_type === "snow" || record.storm_type === "Snow Showers" || record.storm_type === "Isolated Snow Showers"
                            || record.storm_type === "Snow" || record.storm_type === "Chance of Snow" || record.storm_type === "Light Snow"
                            || record.storm_type === "Chance of Sleet/Snow" || record.storm_type === "Scattered Snow Showers" ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: '7px' }}>
                                <div style={{
                                    backgroundColor: bg,
                                    height: '30px',
                                    width: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${color}`,
                                    borderRadius: '4px',
                                    color: color
                                }}>
                                    <IconSnowflake size={18} stroke={2} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', fontSize: '16px' }}>{record.storm_type === "snow" ? "Snow" : record.storm_type}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>Severity: {record.storm_severity}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>
                                        Date: {new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(new Date(record.storm_date))}
                                    </span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>ID: {record.storm_id}</span>
                                </div>
                            </div>
                        ) : record.storm_type === "freezing_rain" || record.storm_type === "Wintry Mix" || record.storm_type === "Chance of Wintry Mix" || record.storm_type === "Fog"
                            || record.storm_type === "Isolated Freezing Rain" || record.storm_type === "Chance of Freezing Rain" ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: '7px' }}>
                                <div style={{
                                    backgroundColor: bg,
                                    height: '30px',
                                    width: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${color}`,
                                    borderRadius: '4px',
                                    color: color
                                }}>
                                    <IconCloudRain size={18} stroke={2} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', fontSize: '16px' }}>{record.storm_type === "freezing_rain" ? "Freezing Rain" : record.storm_type}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>Severity: {record.storm_severity}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>
                                        Date: {new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(new Date(record.storm_date))}
                                    </span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>ID: {record.storm_id}</span>
                                </div>
                            </div>
                        ) : record.storm_type === "salt" ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: '7px' }}>
                                <div style={{
                                    backgroundColor: bg,
                                    height: '30px',
                                    width: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${color}`,
                                    borderRadius: '4px',
                                    color: color
                                }}>
                                    <IconGrain size={18} stroke={2} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', fontSize: '16px' }}>Salting</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>Severity: {record.storm_severity}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>
                                        Date: {new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(new Date(record.storm_date))}
                                    </span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>ID: {record.storm_id}</span>
                                </div>
                            </div>
                        ) : (
                            <div style={{ backgroundColor: '#FDEAEA', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #FF4D4E', borderRadius: '4px', color: "#FF4D4E" }}>
                                <IconTool size={18} stroke={2} />
                            </div>
                        )}
                    </div>
                )
            }
        },
        {
            title: "Vehicle",
            key: "vehicle",
            width: 80,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', width: '100%', height: '50px' }}>
                    <span style={{ fontWeight: '400', fontSize: '16px' }}><IconTractor size={14} style={{ marginRight: '3px' }} />{record.vehicle_name}</span>
                </div>
            ),
        },
        {
            title: 'Staff Assigned',
            key: 'staff_assigned',
            width: 130,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', height: '85px' }}>
                    {record.staff_details.slice(0, 3).map((staff) => (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flexDirection: 'row',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: "ellipsis",
                                maxWidth: '250px'
                            }}
                            key={staff.staff_id}
                        >
                            <span style={{ fontWeight: '400', fontSize: '16px', marginRight: "4px" }}>
                                <IconUser size={14} style={{ marginRight: '3px' }} />
                                {staff.staff_name}
                            </span>
                        </div>
                    ))}

                    {/* Show "+ x more staff" if there are more than 3 staff */}
                    {record.staff_details.length > 3 && (
                        <span style={{ fontSize: '14px', color: '#888' }}>
                            + {record.staff_details.length - 3} more staff
                        </span>
                    )}

                    {record.staff_details.length === 0 && 'No Staff Assigned'}
                </div>
            ),
        },
        {
            title: 'Sites Assigned',
            key: 'sites_assigned',
            width: 180,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', height: '85px' }}>
                    {record.site_details.slice(0, 3).map((site) => (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flexDirection: 'row',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: "ellipsis",
                                maxWidth: '250px'
                            }}
                            key={site.site_id}
                        >
                            <span style={{ fontWeight: '400', fontSize: '16px', marginRight: "4px" }}>
                                <IconBuildingFactory2 size={14} stroke={1.4} style={{ marginRight: '3px' }} />
                                {site.client_name}
                            </span>
                            <span style={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: "ellipsis" }}>
                                - {site.site_name}
                            </span>
                        </div>
                    ))}

                    {/* Show "+ x more sites" if there are more than 3 sites */}
                    {record.site_details.length > 3 && (
                        <span style={{ fontSize: '14px', color: '#888' }}>
                            + {record.site_details.length - 3} more sites
                        </span>
                    )}

                    {record.site_details.length === 0 && 'No Sites Assigned'}
                </div>
            ),
        },
        {
            title: "Dispatched At",
            dataIndex: "dispatched_at",
            key: "dispatched_at",
            width: 100,
            render: (dispatched_at) =>
                dispatched_at ? <div style={{ display: 'flex', height: "50px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                    <span>{moment(dispatched_at).format("MMMM Do YYYY")} </span>
                    <span>{moment(dispatched_at).format("h:mm:ss a")}</span>
                </div> : <div style={{ display: 'flex', height: "85px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>N/A</div>,
            sorter: (a, b) => moment(a.dispatched_at).unix() - moment(b.dispatched_at).unix(),
        },
        {
            title: "Dispatched For",
            dataIndex: "dispatched_for",
            key: "dispatched_for",
            width: 100,
            render: (dispatched_for) =>
                dispatched_for ? <div style={{ display: 'flex', height: "50px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                    <span>{moment(dispatched_for).format("MMMM Do YYYY")} </span> <span>{moment(dispatched_for).format("h:mm a")}</span>
                </div> : <div style={{ display: 'flex', height: "85px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>N/A</div>,
            sorter: (a, b) => moment(a.dispatched_for).unix() - moment(b.dispatched_for).unix(),
        },
        {
            title: "Overall Confirmation",
            key: "overall_staff_confirmation",
            width: 80,
            render: (record) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', width: '100%', height: '50px' }}>
                    {record.overall_staff_confirmation === "Confirmed" && (
                        <div style={{ backgroundColor: '#E5F2EC', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #034832', borderRadius: '4px', color: "#034832" }}>
                            <IconCheck size={18} stroke={2} />
                        </div>
                    )}
                    {record.overall_staff_confirmation === "Not Confirmed" && (
                        <Tooltip title={(record.staff_details.map((staff) => (<><li>{staff.staff_name} - {staff.status}</li></>)))}>
                            <div style={{ backgroundColor: '#FDEAEA', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #FF4D4E', borderRadius: '4px', color: "#FF4D4E" }}>
                                <IconX size={18} stroke={2} />
                            </div>
                        </Tooltip>
                    )}
                    {record.overall_staff_confirmation === "Created" && (
                        <div style={{ backgroundColor: '#ebebeb', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #034832', borderRadius: '4px', color: "#034832" }}>
                            <IconCheck size={18} stroke={2} />
                        </div>
                    )}
                </div>
            ),
        }
    ]

    const walkwayCrewsExpandedRow = (record) => (
        <div style={{ width: '100%', display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', flexDirection: 'row' }}>
            {/* Staff */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingRight: '30px', borderRight: "0.5px solid #e0e0e0" }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Staff Members:</span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '10px' }}>
                    {record.staff_details.map((staff, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexDirection: 'row', width: '400px', border: '1px solid #e0e0e0', borderRadius: '5px', padding: '5px 10px', backgroundColor: getStaffCardBackgroundColor(staff.status) }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                    flexDirection: 'column',
                                }}
                            >
                                <span style={{ fontWeight: '500', fontSize: '16px', marginRight: "4px" }}>
                                    <IconUser size={14} style={{ marginRight: '3px' }} />
                                    {staff.staff_name}
                                </span>
                                <span style={{ fontSize: '14px' }}>
                                    {staff.staff_position}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', justifyContent: 'flex-start', gap: "5px" }}>
                                <span style={{ fontSize: '14px' }}><span style={{ fontWeight: '500' }}>Contact Number:</span>  {staff.staff_phone}</span>
                                <span style={{ fontSize: '14px' }}><span style={{ fontWeight: '500' }}>Message Status:</span> <Tag>{staff.status === "undelivered" ? "Error In Delivery" : staff.status}</Tag><Tooltip onClick={() => handleResendDispatchMessage(record.dispatch_log_id, staff.staff_id, "walkwayCrew")} title="Resend Message"><IconMessage size={14} style={{ cursor: 'pointer', paddingTop: '5px' }} /></Tooltip></span>
                            </div>
                            <div style={{ marginTop: '4px', display: 'flex', flexDirection: "column", gap: '4px' }}>
                                {/* Confirm Button - For all statuses with confirmation */}
                                <Popconfirm
                                    title="Are you sure you want to confirm this dispatch response?"
                                    onConfirm={() => handleStatusChange(staff.dispatched_staff_id, "Confirmed")}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        type="primary"
                                        style={{ backgroundColor: '#034832', borderColor: '#034832', color: 'white', width: '80px' }}
                                    >
                                        Confirm
                                    </Button>
                                </Popconfirm>

                                {/* Deny Button - For all statuses with confirmation */}
                                <Popconfirm
                                    title="Are you sure you want to deny this dispatch response?"
                                    onConfirm={() => handleStatusChange(staff.dispatched_staff_id, "Denied")}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        type="primary"
                                        style={{ backgroundColor: '#FF4D4E', borderColor: '#FF4D4E', color: 'white', width: '80px' }}
                                    >
                                        Deny
                                    </Button>
                                </Popconfirm>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Sites */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px', paddingRight: '30px', borderRight: "0.5px solid #e0e0e0" }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Sites:</span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                    {record.site_details.map((site, index) => (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                flexDirection: 'column',
                                border: '1px solid #e0e0e0',
                                borderRadius: '5px',
                                padding: '5px 10px',
                                backgroundColor: '#f0f0f0',
                                width: "250px",
                                position: 'relative',
                            }}
                            key={index}
                        >
                            <span style={{ fontWeight: '500', fontSize: '16px', marginRight: "4px" }}>
                                <IconBuildingFactory2 size={14} stroke={1.4} style={{ marginRight: '3px' }} />
                                {site.client_name}
                            </span>
                            <span style={{ fontSize: '14px', }}>
                                {site.site_name}
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: '500', position: 'absolute', top: '0', right: '0', marginTop: '5px', marginRight: '10px' }}>
                                {site.site_region}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            {/* Equipment */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px', paddingRight: '30px', borderRight: "0.5px solid #e0e0e0" }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Equipment:</span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                    {record.equipment_details.map((equipment, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', width: '250px', border: '1px solid #e0e0e0', borderRadius: '5px', padding: '5px 10px', backgroundColor: '#f0f0f0' }}>
                            <span style={{ fontWeight: '500', fontSize: '16px' }}>
                                <IconTractor size={14} stroke={1.4} style={{ marginRight: '3px' }} />
                                {equipment.equipment_name}
                            </span>
                            <span style={{ fontSize: '14px' }}>{equipment.equipment_type_name}</span>
                        </div>
                    ))}
                </div>
            </div>
            {/* Notes */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px', paddingRight: '30px', borderRight: '0.5px solid #e0e0e0' }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px', width: "400px" }}>
                    Notes:
                </span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ backgroundColor: '#f5f5f5', borderRadius: '4px', padding: '8px', whiteSpace: 'pre-wrap', wordWrap: 'break-word', minHeight: '40px', width: '400px' }}>
                        <span style={{ fontSize: '14px' }}>
                            {record.message
                                ? record.message.length > 500
                                    ? `${record.message.slice(0, 500)}...`
                                    : record.message
                                : "N/A"}
                        </span>
                    </div>
                </div>
            </div>
            {/* Generate Timesheet and Storm Time */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px' }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Storm date and time:</span>
                <span style={{ fontSize: '14px', marginBottom: '5px' }}>
                    <b>Start:</b> {record.storm_date ? dayjs.utc(record.storm_date).tz('America/New_York').format('MMMM Do YYYY, h:mm A') : 'N/A'}
                </span>
                <span style={{ fontSize: '14px', marginBottom: '10px' }}>
                    <b>End:</b> {record.storm_date_end ? dayjs.utc(record.storm_date_end).tz('America/New_York').format('MMMM Do YYYY, h:mm A') : 'N/A'}
                </span>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Generate Timesheet:</span>
                {record.storm_date && record.storm_date_end ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                        {record.staff_details.filter(staff => staff.status === "Confirmed").length > 0 ? (
                            record.staff_details
                                .filter(staff => staff.status === "Confirmed")
                                .map((staff, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                        <span style={{ fontSize: '14px' }}>
                                            <div
                                                className={styles.tableButton}
                                                onClick={() => generateStaffTimesheet(record.storm_date, record.storm_date_end, staff.staff_name, record.vehicle_name)}
                                                style={{ fontSize: '14px', padding: '8px 15px', cursor: 'pointer', marginBottom: 10 }}
                                            >
                                                Timesheet - {staff.staff_name}
                                            </div>
                                        </span>
                                    </div>
                                ))
                        ) : (
                            <div>
                                <span style={{ color: 'red' }}><em>* </em></span>
                                <span style={{ fontStyle: 'italic' }}>No confirmed staff</span>
                                <span style={{ color: 'red' }}><em> *</em></span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <span style={{ color: 'red' }}><em>* </em></span>
                        <span><em>Associated storm's start time and end time must be specified before generating timesheet</em></span>
                        <span style={{ color: 'red' }}><em> *</em></span>
                    </div>
                )}
            </div>
        </div>
    )

    const saltingRoutesColumns = [
        {
            title: "Route Name",
            dataIndex: "name", // Specifies the data field
            key: "name",
            width: 100,
            sorter: (a, b) => {
                const nameA = a.name ? a.name.toLowerCase() : "";
                const nameB = b.name ? b.name.toLowerCase() : "";
                return nameA.localeCompare(nameB);
            },
            sortDirections: ['ascend', 'descend'],
            render: (text) => <b>{text || ""}</b>, // Renders the text in bold, shows "N/A" if null
        },
        {
            title: "Region",
            dataIndex: "region", // Use dataIndex for better integration with Ant Design
            key: "region",
            width: 60,
            sorter: (a, b) => {
                // Handle cases where region might be null or undefined
                const regionA = a.region ? a.region.toLowerCase() : "";
                const regionB = b.region ? b.region.toLowerCase() : "";
                if (regionA < regionB) return -1;
                if (regionA > regionB) return 1;
                return 0;
            },
            sortDirections: ['ascend', 'descend'],
            render: (text) => <b>{text}</b>,
        },
        {
            title: "Event",
            key: "storm_type",
            width: 180,
            // render: (record) => <span>{record.storm_type} ({record.storm_severity})</span>,
            render: (text, record) => {
                const { color, bg } = getSeverityColor(record.storm_severity);
                return (
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', width: '100%', height: '50px' }}>
                        {record.storm_type === "snow" || record.storm_type === "Snow Showers" || record.storm_type === "Isolated Snow Showers"
                            || record.storm_type === "Snow" || record.storm_type === "Chance of Snow" || record.storm_type === "Light Snow"
                            || record.storm_type === "Chance of Sleet/Snow" || record.storm_type === "Scattered Snow Showers" ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: '7px' }}>
                                <div style={{
                                    backgroundColor: bg,
                                    height: '30px',
                                    width: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${color}`,
                                    borderRadius: '4px',
                                    color: color
                                }}>
                                    <IconSnowflake size={18} stroke={2} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', fontSize: '16px' }}>{record.storm_type === "snow" ? "Snow" : record.storm_type}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>Severity: {record.storm_severity}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>
                                        Date: {new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(new Date(record.storm_date))}
                                    </span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>ID: {record.storm_id}</span>
                                </div>
                            </div>
                        ) : record.storm_type === "freezing_rain" || record.storm_type === "Wintry Mix" || record.storm_type === "Chance of Wintry Mix" || record.storm_type === "Fog"
                            || record.storm_type === "Isolated Freezing Rain" || record.storm_type === "Chance of Freezing Rain" ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: '7px' }}>
                                <div style={{
                                    backgroundColor: bg,
                                    height: '30px',
                                    width: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${color}`,
                                    borderRadius: '4px',
                                    color: color
                                }}>
                                    <IconCloudRain size={18} stroke={2} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', fontSize: '16px' }}>{record.storm_type === "freezing_rain" ? "Freezing Rain" : record.storm_type}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>Severity: {record.storm_severity}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>
                                        Date: {new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(new Date(record.storm_date))}
                                    </span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>ID: {record.storm_id}</span>
                                </div>
                            </div>
                        ) : record.storm_type === "salt" ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: '7px' }}>
                                <div style={{
                                    backgroundColor: bg,
                                    height: '30px',
                                    width: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${color}`,
                                    borderRadius: '4px',
                                    color: color
                                }}>
                                    <IconGrain size={18} stroke={2} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', fontSize: '16px' }}>Salting</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>Severity: {record.storm_severity}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>
                                        Date: {new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(new Date(record.storm_date))}
                                    </span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>ID: {record.storm_id}</span>
                                </div>
                            </div>
                        ) : (
                            <div style={{ backgroundColor: '#FDEAEA', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #FF4D4E', borderRadius: '4px', color: "#FF4D4E" }}>
                                <IconTool size={18} stroke={2} />
                            </div>
                        )}
                    </div>
                )
            }
        },
        {
            title: "Vehicle",
            key: "vehicle",
            width: 100,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', width: '100%', height: '50px' }}>
                    <span style={{ fontWeight: '400', fontSize: '16px' }}><IconTractor size={14} style={{ marginRight: '3px' }} />{record.vehicle_name}</span>
                </div>
            ),
        },
        {
            title: 'Staff Assigned',
            key: 'staff_assigned',
            width: 130,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', height: '85px' }}>
                    {record.staff_details.slice(0, 3).map((staff) => (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flexDirection: 'row',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: "ellipsis",
                                maxWidth: '250px'
                            }}
                            key={staff.staff_id}
                        >
                            <span style={{ fontWeight: '400', fontSize: '16px', marginRight: "4px" }}>
                                <IconUser size={14} style={{ marginRight: '3px' }} />
                                {staff.staff_name}
                            </span>
                        </div>
                    ))}

                    {/* Show "+ x more staff" if there are more than 3 staff */}
                    {record.staff_details.length > 3 && (
                        <span style={{ fontSize: '14px', color: '#888' }}>
                            + {record.staff_details.length - 3} more staff
                        </span>
                    )}

                    {record.staff_details.length === 0 && 'No Staff Assigned'}
                </div>
            ),
        },
        {
            title: 'Sites Assigned',
            key: 'sites_assigned',
            width: 180,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', height: '85px' }}>
                    {record.site_details.slice(0, 3).map((site) => (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flexDirection: 'row',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: "ellipsis",
                                maxWidth: '250px'
                            }}
                            key={site.site_id}
                        >
                            <span style={{ fontWeight: '400', fontSize: '16px', marginRight: "4px" }}>
                                <IconBuildingFactory2 size={14} stroke={1.4} style={{ marginRight: '3px' }} />
                                {site.site_name}
                            </span>
                        </div>
                    ))}

                    {/* Show "+ x more sites" if there are more than 3 sites */}
                    {record.site_details.length > 3 && (
                        <span style={{ fontSize: '14px', color: '#888' }}>
                            + {record.site_details.length - 3} more sites
                        </span>
                    )}

                    {record.site_details.length === 0 && 'No Sites Assigned'}
                </div>
            ),
        },
        {
            title: "Dispatched At",
            dataIndex: "dispatched_at",
            key: "dispatched_at",
            width: 100,
            render: (dispatched_at) =>
                dispatched_at ? <div style={{ display: 'flex', height: "50px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                    <span>{moment(dispatched_at).format("MMMM Do YYYY")} </span>
                    <span>{moment(dispatched_at).format("h:mm:ss a")}</span>
                </div> : <div style={{ display: 'flex', height: "85px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>N/A</div>,
            sorter: (a, b) => moment(a.dispatched_at).unix() - moment(b.dispatched_at).unix(),
        },
        {
            title: "Dispatched For",
            dataIndex: "dispatched_for",
            key: "dispatched_for",
            width: 100,
            render: (dispatched_for) =>
                dispatched_for ? <div style={{ display: 'flex', height: "50px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                    <span>{moment(dispatched_for).format("MMMM Do YYYY")} </span>
                    <span>{moment(dispatched_for).format("h:mm:ss a")}</span>
                </div> : <div style={{ display: 'flex', height: "85px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>N/A</div>,
            sorter: (a, b) => moment(a.dispatched_for).unix() - moment(b.dispatched_for).unix(),
        },
        {
            title: "Overall Confirmation",
            key: "overall_staff_confirmation",
            width: 80,
            render: (record) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', width: '100%', height: '50px' }}>
                    {record.overall_staff_confirmation === "Confirmed" && (
                        <div style={{ backgroundColor: '#E5F2EC', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #034832', borderRadius: '4px', color: "#034832" }}>
                            <IconCheck size={18} stroke={2} />
                        </div>
                    )}
                    {record.overall_staff_confirmation === "Not Confirmed" && (
                        <Tooltip title={(record.staff_details.map((staff) => (<><li>{staff.staff_name} - {staff.status}</li></>)))}>
                            <div style={{ backgroundColor: '#FDEAEA', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #FF4D4E', borderRadius: '4px', color: "#FF4D4E" }}>
                                <IconX size={18} stroke={2} />
                            </div>
                        </Tooltip>
                    )}
                    {record.overall_staff_confirmation === "Created" && (
                        <div style={{ backgroundColor: '#ebebeb', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #034832', borderRadius: '4px', color: "#034832" }}>
                            <IconCheck size={18} stroke={2} />
                        </div>
                    )}
                </div>
            ),
        }
    ]

    const saltingRoutesExpandedRow = (record) => (
        <div style={{ width: '100%', display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', flexDirection: 'row' }}>
            {/* Staff */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingRight: '30px', borderRight: "0.5px solid #e0e0e0" }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Staff Members:</span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '10px' }}>
                    {record.staff_details.map((staff, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexDirection: 'row', width: '400px', border: '1px solid #e0e0e0', borderRadius: '5px', padding: '5px 10px', backgroundColor: getStaffCardBackgroundColor(staff.status) }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                    flexDirection: 'column',
                                }}
                            >
                                <span style={{ fontWeight: '500', fontSize: '16px', marginRight: "4px" }}>
                                    <IconUser size={14} style={{ marginRight: '3px' }} />
                                    {staff.staff_name}
                                </span>
                                <span style={{ fontSize: '14px' }}>
                                    {staff.staff_position}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', justifyContent: 'flex-start', gap: "5px" }}>
                                <span style={{ fontSize: '14px' }}><span style={{ fontWeight: '500' }}>Contact Number:</span>  {staff.staff_phone}</span>
                                <span style={{ fontSize: '14px' }}><span style={{ fontWeight: '500' }}>Message Status:</span> <Tag>{staff.status === "undelivered" ? "Error In Delivery" : staff.status}</Tag><Tooltip onClick={() => handleResendDispatchMessage(record.dispatch_log_id, staff.staff_id, "saltingRoute")} title="Resend Message"><IconMessage size={14} style={{ cursor: 'pointer', paddingTop: '5px' }} /></Tooltip></span>
                            </div>
                            <div style={{ marginTop: '4px', display: 'flex', flexDirection: "column", gap: '4px' }}>
                                {/* Confirm Button - For all statuses with confirmation */}
                                <Popconfirm
                                    title="Are you sure you want to confirm this dispatch response?"
                                    onConfirm={() => handleStatusChange(staff.dispatched_staff_id, "Confirmed")}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        type="primary"
                                        style={{ backgroundColor: '#034832', borderColor: '#034832', color: 'white', width: '80px' }}
                                    >
                                        Confirm
                                    </Button>
                                </Popconfirm>

                                {/* Deny Button - For all statuses with confirmation */}
                                <Popconfirm
                                    title="Are you sure you want to deny this dispatch response?"
                                    onConfirm={() => handleStatusChange(staff.dispatched_staff_id, "Denied")}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        type="primary"
                                        style={{ backgroundColor: '#FF4D4E', borderColor: '#FF4D4E', color: 'white', width: '80px' }}
                                    >
                                        Deny
                                    </Button>
                                </Popconfirm>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Sites */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px', paddingRight: '30px', borderRight: "0.5px solid #e0e0e0" }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Sites:</span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                    {record.site_details.map((site, index) => (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                flexDirection: 'column',
                                border: '1px solid #e0e0e0',
                                borderRadius: '5px',
                                padding: '5px 10px',
                                backgroundColor: '#f0f0f0',
                                width: "350px",
                                position: 'relative',
                            }}
                            key={index}
                        >
                            <span style={{ fontWeight: '500', fontSize: '16px', marginRight: "4px" }}>
                                <IconBuildingFactory2 size={14} stroke={1.4} style={{ marginRight: '3px' }} />
                                {site.client_name}
                            </span>
                            <span style={{ fontSize: '14px', }}>
                                {site.site_name}
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: '500', position: 'absolute', top: '0', right: '0', marginTop: '5px', marginRight: '10px' }}>
                                {site.site_region}
                            </span>

                        </div>
                    ))}
                </div>
            </div>
            {/* Notes */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px', paddingRight: '30px', borderRight: '0.5px solid #e0e0e0' }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px', width: "400px" }}>
                    Notes:
                </span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ backgroundColor: '#f5f5f5', borderRadius: '4px', padding: '8px', whiteSpace: 'pre-wrap', wordWrap: 'break-word', minHeight: '40px', width: '400px' }}>
                        <span style={{ fontSize: '14px' }}>
                            {record.message
                                ? record.message.length > 500
                                    ? `${record.message.slice(0, 500)}...`
                                    : record.message
                                : "N/A"}
                        </span>
                    </div>
                </div>
            </div>
            {/* Generate Timesheet and Storm Time */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px' }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Storm date and time:</span>
                <span style={{ fontSize: '14px', marginBottom: '5px' }}>
                    <b>Start:</b> {record.storm_date ? dayjs.utc(record.storm_date).tz('America/New_York').format('MMMM Do YYYY, h:mm A') : 'N/A'}
                </span>
                <span style={{ fontSize: '14px', marginBottom: '10px' }}>
                    <b>End:</b> {record.storm_date_end ? dayjs.utc(record.storm_date_end).tz('America/New_York').format('MMMM Do YYYY, h:mm A') : 'N/A'}
                </span>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Generate Timesheet:</span>
                {record.storm_date && record.storm_date_end ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                        {record.staff_details.filter(staff => staff.status === "Confirmed").length > 0 ? (
                            record.staff_details
                                .filter(staff => staff.status === "Confirmed")
                                .map((staff, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                        <span style={{ fontSize: '14px' }}>
                                            <div
                                                className={styles.tableButton}
                                                onClick={() => generateStaffTimesheet(record.storm_date, record.storm_date_end, staff.staff_name, record.vehicle_name)}
                                                style={{ fontSize: '14px', padding: '8px 15px', cursor: 'pointer', marginBottom: 10 }}
                                            >
                                                Timesheet - {staff.staff_name}
                                            </div>
                                        </span>
                                    </div>
                                ))
                        ) : (
                            <div>
                                <span style={{ color: 'red' }}><em>* </em></span>
                                <span style={{ fontStyle: 'italic' }}>No confirmed staff</span>
                                <span style={{ color: 'red' }}><em> *</em></span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <span style={{ color: 'red' }}><em>* </em></span>
                        <span><em>Associated storm's start time and end time must be specified before generating timesheet</em></span>
                        <span style={{ color: 'red' }}><em> *</em></span>
                    </div>
                )}
            </div>
        </div>
    )

    const loadersColumns = [
        {
            title: "Region",
            dataIndex: "region", // Use dataIndex for better integration with Ant Design
            key: "region",
            width: 60,
            sorter: (a, b) => {
                // Handle cases where region might be null or undefined
                const regionA = a.region ? a.region.toLowerCase() : "";
                const regionB = b.region ? b.region.toLowerCase() : "";
                if (regionA < regionB) return -1;
                if (regionA > regionB) return 1;
                return 0;
            },
            sortDirections: ['ascend', 'descend'],
            render: (text) => <b>{text}</b>,
        },
        {
            title: "Event",
            key: "storm_type",
            width: 180,
            // render: (record) => <span>{record.storm_type} ({record.storm_severity})</span>,
            render: (text, record) => {
                const { color, bg } = getSeverityColor(record.storm_severity);
                return (
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', width: '100%', height: '50px' }}>
                        {record.storm_type === "snow" || record.storm_type === "Snow Showers" || record.storm_type === "Isolated Snow Showers"
                            || record.storm_type === "Snow" || record.storm_type === "Chance of Snow" || record.storm_type === "Light Snow"
                            || record.storm_type === "Chance of Sleet/Snow" || record.storm_type === "Scattered Snow Showers" ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: '7px' }}>
                                <div style={{
                                    backgroundColor: bg,
                                    height: '30px',
                                    width: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${color}`,
                                    borderRadius: '4px',
                                    color: color
                                }}>
                                    <IconSnowflake size={18} stroke={2} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', fontSize: '16px' }}>{record.storm_type === "snow" ? "Snow" : record.storm_type}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>Severity: {record.storm_severity}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>
                                        Date: {new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(new Date(record.storm_date))}
                                    </span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>ID: {record.storm_id}</span>
                                </div>
                            </div>
                        ) : record.storm_type === "freezing_rain" || record.storm_type === "Wintry Mix" || record.storm_type === "Chance of Wintry Mix" || record.storm_type === "Fog"
                            || record.storm_type === "Isolated Freezing Rain" || record.storm_type === "Chance of Freezing Rain" ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: '7px' }}>
                                <div style={{
                                    backgroundColor: bg,
                                    height: '30px',
                                    width: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${color}`,
                                    borderRadius: '4px',
                                    color: color
                                }}>
                                    <IconCloudRain size={18} stroke={2} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', fontSize: '16px' }}>{record.storm_type === "freezing_rain" ? "Freezing Rain" : record.storm_type}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>Severity: {record.storm_severity}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>
                                        Date: {new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(new Date(record.storm_date))}
                                    </span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>ID: {record.storm_id}</span>
                                </div>
                            </div>
                        ) : record.storm_type === "salt" ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: '7px' }}>
                                <div style={{
                                    backgroundColor: bg,
                                    height: '30px',
                                    width: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${color}`,
                                    borderRadius: '4px',
                                    color: color
                                }}>
                                    <IconGrain size={18} stroke={2} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', fontSize: '16px' }}>Salting</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>Severity: {record.storm_severity}</span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>
                                        Date: {new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric' }).format(new Date(record.storm_date))}
                                    </span>
                                    <span style={{ fontSize: '14px', marginTop: '-5px' }}>ID: {record.storm_id}</span>
                                </div>
                            </div>
                        ) : (
                            <div style={{ backgroundColor: '#FDEAEA', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #FF4D4E', borderRadius: '4px', color: "#FF4D4E" }}>
                                <IconTool size={18} stroke={2} />
                            </div>
                        )}
                    </div>
                )
            }
        },
        {
            title: "Vehicle",
            key: "vehicle",
            width: 100,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', width: '100%', height: '50px' }}>
                    <span style={{ fontWeight: '400', fontSize: '16px' }}><IconTractor size={14} style={{ marginRight: '3px' }} />{record.vehicle_name}</span>
                </div>
            ),
        },
        {
            title: 'Staff Assigned',
            key: 'staff_assigned',
            width: 130,
            render: (text, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                    {record.staff_details.length > 0 ? (
                        record.staff_details.map((staff) => (
                            <div key={staff.staff_id} style={{ marginBottom: '4px' }}>
                                <span><IconUser size={14} style={{ marginRight: '3px' }} /></span>
                                <span style={{ fontWeight: '400', fontSize: '16px' }}>{staff.staff_name}</span>
                            </div>
                        ))
                    ) : (
                        <span>No Staff Assigned</span>
                    )}
                </div>
            ),
        },
        {
            title: 'Sites Assigned',
            key: 'sites_assigned',
            width: 180,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column', height: '85px' }}>
                    {record.site_details.slice(0, 3).map((site) => (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flexDirection: 'row',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: "ellipsis",
                                maxWidth: '250px'
                            }}
                            key={site.site_id}
                        >
                            <span style={{ fontWeight: '400', fontSize: '16px', marginRight: "4px" }}>
                                <IconBuildingFactory2 size={14} stroke={1.4} style={{ marginRight: '3px' }} />
                                {site.site_name}
                            </span>
                        </div>
                    ))}

                    {/* Show "+ x more sites" if there are more than 3 sites */}
                    {record.site_details.length > 3 && (
                        <span style={{ fontSize: '14px', color: '#888' }}>
                            + {record.site_details.length - 3} more sites
                        </span>
                    )}

                    {record.site_details.length === 0 && 'No Sites Assigned'}
                </div>
            ),
        },
        {
            title: "Dispatched At",
            dataIndex: "dispatched_at",
            key: "dispatched_at",
            width: 100,
            render: (dispatched_at) =>
                dispatched_at ? <div style={{ display: 'flex', height: "50px", alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column' }}>
                    <span>{moment(dispatched_at).format("MMMM Do YYYY")} </span>
                    <span>{moment(dispatched_at).format("h:mm:ss a")}</span>
                </div> : <div style={{ display: 'flex', height: "85px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>N/A</div>,
            sorter: (a, b) => moment(a.dispatched_at).unix() - moment(b.dispatched_at).unix(),
        },
        {
            title: "Dispatched For",
            dataIndex: "dispatched_for",
            key: "dispatched_for",
            width: 100,
            render: (dispatched_for) =>
                dispatched_for ? <div style={{ display: 'flex', height: "50px", alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'column' }}>
                    <span>{moment(dispatched_for).format("MMMM Do YYYY")} </span>
                    <span>{moment(dispatched_for).format("h:mm:ss a")}</span>
                </div> : <div style={{ display: 'flex', height: "85px", alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>N/A</div>,
            sorter: (a, b) => moment(a.dispatched_for).unix() - moment(b.dispatched_for).unix(),
        },
        {
            title: "Overall Confirmation",
            key: "overall_staff_confirmation",
            width: 80,
            render: (record) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', width: '100%', height: '50px' }}>
                    {record.overall_staff_confirmation === "Confirmed" && (
                        <div style={{ backgroundColor: '#E5F2EC', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #034832', borderRadius: '4px', color: "#034832" }}>
                            <IconCheck size={18} stroke={2} />
                        </div>
                    )}
                    {record.overall_staff_confirmation === "Not Confirmed" && (
                        <Tooltip title={(record.staff_details.map((staff) => (<><li>{staff.staff_name} - {staff.status}</li></>)))}>
                            <div style={{ backgroundColor: '#FDEAEA', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #FF4D4E', borderRadius: '4px', color: "#FF4D4E" }}>
                                <IconX size={18} stroke={2} />
                            </div>
                        </Tooltip>
                    )}
                    {record.overall_staff_confirmation === "Created" && (
                        <div style={{ backgroundColor: '#ebebeb', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #034832', borderRadius: '4px', color: "#034832" }}>
                            <IconCheck size={18} stroke={2} />
                        </div>
                    )}
                </div>
            ),
        }
    ]

    const loadersExpandedRow = (record) => (
        <div style={{ width: '100%', display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', flexDirection: 'row' }}>
            {/* Staff */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingRight: '30px', borderRight: "0.5px solid #e0e0e0" }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Staff Members:</span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '10px' }}>
                    {record.staff_details.map((staff, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexDirection: 'row', width: '400px', border: '1px solid #e0e0e0', borderRadius: '5px', padding: '5px 10px', backgroundColor: getStaffCardBackgroundColor(staff.status) }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                    flexDirection: 'column',
                                }}
                            >
                                <span style={{ fontWeight: '500', fontSize: '16px', marginRight: "4px" }}>
                                    <IconUser size={14} style={{ marginRight: '3px' }} />
                                    {staff.staff_name}
                                </span>
                                <span style={{ fontSize: '14px' }}>
                                    {staff.staff_position}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', justifyContent: 'flex-start', gap: "5px" }}>
                                <span style={{ fontSize: '14px' }}><span style={{ fontWeight: '500' }}>Contact Number:</span>  {staff.staff_phone}</span>
                                <span style={{ fontSize: '14px' }}><span style={{ fontWeight: '500' }}>Message Status:</span> <Tag>{staff.status === "undelivered" ? "Error In Delivery" : staff.status}</Tag><Tooltip onClick={() => handleResendDispatchMessage(record.dispatch_log_id, staff.staff_id, "loader")} title="Resend Message"><IconMessage size={14} style={{ cursor: 'pointer', paddingTop: '5px' }} /></Tooltip></span>
                            </div>
                            <div style={{ marginTop: '4px', display: 'flex', flexDirection: "column", gap: '4px' }}>
                                {/* Confirm Button - For all statuses with confirmation */}
                                <Popconfirm
                                    title="Are you sure you want to confirm this dispatch response?"
                                    onConfirm={() => handleStatusChange(staff.dispatched_staff_id, "Confirmed")}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        type="primary"
                                        style={{ backgroundColor: '#034832', borderColor: '#034832', color: 'white', width: '80px' }}
                                    >
                                        Confirm
                                    </Button>
                                </Popconfirm>

                                {/* Deny Button - For all statuses with confirmation */}
                                <Popconfirm
                                    title="Are you sure you want to deny this dispatch response?"
                                    onConfirm={() => handleStatusChange(staff.dispatched_staff_id, "Deined")}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        type="primary"
                                        style={{ backgroundColor: '#FF4D4E', borderColor: '#FF4D4E', color: 'white', width: '80px' }}
                                    >
                                        Deny
                                    </Button>
                                </Popconfirm>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Sites */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px', paddingRight: '30px', borderRight: "0.5px solid #e0e0e0" }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Sites:</span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                    {record.site_details.map((site, index) => (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                flexDirection: 'column',
                                border: '1px solid #e0e0e0',
                                borderRadius: '5px',
                                padding: '5px 10px',
                                backgroundColor: '#f0f0f0',
                                width: "350px",
                                position: 'relative',
                            }}
                            key={index}
                        >
                            <span style={{ fontWeight: '500', fontSize: '16px', marginRight: "4px" }}>
                                <IconBuildingFactory2 size={14} stroke={1.4} style={{ marginRight: '3px' }} />
                                {site.client_name}
                            </span>
                            <span style={{ fontSize: '14px', }}>
                                {site.site_name}
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: '500', position: 'absolute', top: '0', right: '0', marginTop: '5px', marginRight: '10px' }}>
                                {site.site_region}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            {/* Notes */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px', paddingRight: '30px', borderRight: '0.5px solid #e0e0e0' }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px', width: "400px" }}>
                    Notes:
                </span>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ backgroundColor: '#f5f5f5', borderRadius: '4px', padding: '8px', whiteSpace: 'pre-wrap', wordWrap: 'break-word', minHeight: '40px', width: '400px' }}>
                        <span style={{ fontSize: '14px' }}>
                            {record.message
                                ? record.message.length > 500
                                    ? `${record.message.slice(0, 500)}...`
                                    : record.message
                                : "N/A"}
                        </span>
                    </div>
                </div>
            </div>
            {/* Generate Timesheet and Storm Time */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingLeft: '30px' }}>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Storm date and time:</span>
                <span style={{ fontSize: '14px', marginBottom: '5px' }}>
                    <b>Start:</b> {record.storm_date ? dayjs.utc(record.storm_date).tz('America/New_York').format('MMMM Do YYYY, h:mm A') : 'N/A'}
                </span>
                <span style={{ fontSize: '14px', marginBottom: '10px' }}>
                    <b>End:</b> {record.storm_date_end ? dayjs.utc(record.storm_date_end).tz('America/New_York').format('MMMM Do YYYY, h:mm A') : 'N/A'}
                </span>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Generate Timesheet:</span>
                {record.storm_date && record.storm_date_end ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '5px' }}>
                        {record.staff_details.filter(staff => staff.status === "Confirmed").length > 0 ? (
                            record.staff_details
                                .filter(staff => staff.status === "Confirmed")
                                .map((staff, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                                        <span style={{ fontSize: '14px' }}>
                                            <div
                                                className={styles.tableButton}
                                                onClick={() => generateStaffTimesheet(record.storm_date, record.storm_date_end, staff.staff_name, record.vehicle_name)}
                                                style={{ fontSize: '14px', padding: '8px 15px', cursor: 'pointer', marginBottom: 10 }}
                                            >
                                                Timesheet - {staff.staff_name}
                                            </div>
                                        </span>
                                    </div>
                                ))
                        ) : (
                            <div>
                                <span style={{ color: 'red' }}><em>* </em></span>
                                <span style={{ fontStyle: 'italic' }}>No confirmed staff</span>
                                <span style={{ color: 'red' }}><em> *</em></span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <span style={{ color: 'red' }}><em>* </em></span>
                        <span><em>Associated storm's start time and end time must be specified before generating timesheet</em></span>
                        <span style={{ color: 'red' }}><em> *</em></span>
                    </div>
                )}
            </div>
        </div>
    )

    const [currentLogs, setCurrentLogs] = useState([])
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
        totalPages: 0
    });

    useEffect(() => {
        if (!isStormFilterSet) return;
        fetchDispatchLogs(1, pagination.pageSize, objectFilter);
        fetchRegionOptions(objectFilter);
        fetchStormOptions(objectFilter);
      }, [regionFilter, vehicleSearch, staffSearch, siteSearch, stormIdFilter, objectFilter, isStormFilterSet]);
    
      useEffect(() => {
        if (!isStormFilterSet) return;
        fetchCounters("Walkway");
        fetchCounters("Loader");
        fetchCounters("Salting");
      }, [stormIdFilter, isStormFilterSet]);

    useEffect(() => {
        const fetchLatestStorm = async () => {
        try {
            const response = await api.get('dispatch/storm/latest/');
            if (response.data) {
            setStormIdFilter(response.data.storm_id);
            setIsStormFilterSet(true);
            }
        } catch (error) {
            console.error('Error fetching latest storm:', error);
            message.error('Failed to fetch latest storm');
        }
        };
        fetchLatestStorm();
    }, []);

    const fetchCounters = async (type) => {
        try {
            await api.get(`dispatch/dispatchLogs/${type}/counter/`, {
                params: {
                    // If a specific storm is chosen, filter just that storm
                    stormId: stormIdFilter !== 'All' ? stormIdFilter : undefined,
                }
            }).then((response) => {
                setCounter((prevCounter) => ({
                    ...prevCounter, [type]: {
                        unread: response.data.unread,
                        confirmed: response.data.confirmed,
                        denied: response.data.denied
                    }
                }));
            })
        } catch (error) {
            console.error('Error fetching counters:', error);
            message.error('Failed to fetch counters');
        }
    }
    const fetchStormOptions = async (type) => {
        try {
            await api.get(`dispatch/dispatchLogs/${type}/storms/`, {
                params: {
                    staff: staffSearch || undefined,
                    site: siteSearch || undefined,
                    vehicle: vehicleSearch || undefined,
                    region: regionFilter !== 'All' ? regionFilter : undefined,
                }
            }).then((response) => {
                setStormOptions(response.data)
            })
        } catch (error) {
            console.error('Error fetching storms:', error);
            message.error('Failed to fetch storms');
        }
    }
    const fetchRegionOptions = async (type) => {

        try {
            await api.get(`/dispatch/dispatchLogs/${type}/regions/`, {
                params: {
                    staff: staffSearch || undefined,
                    site: siteSearch || undefined,
                    vehicle: vehicleSearch || undefined,
                    stormId: stormIdFilter !== 'All' ? stormIdFilter : undefined,
                }
            }).then((response) => {
                setRegionOptions(response.data);
            });
        } catch (error) {
            console.error('Error fetching regions:', error);
            message.error('Failed to fetch regions');
        }
    }
    const fetchDispatchLogs = async (page = 1, pageSize = 50, type) => {
        setLoading(true);
        try {
            await api.get(`/dispatch/dispatchLogs/${type}/`, {
                params: {
                    region:
                        regionFilter.length === 0 || regionFilter.includes("All")
                        ? undefined
                        : regionFilter.join(','),                    
                    staff: staffSearch || undefined,
                    site: siteSearch || undefined,
                    vehicle: vehicleSearch || undefined,
                    stormId: stormIdFilter !== 'All' ? stormIdFilter : undefined,
                    page: page,
                    pageSize: pageSize
                }
            }).then((response) => {
                setCurrentLogs(response.data.data);
                setPagination({
                    current: response.data.page,
                    pageSize: response.data.pageSize,
                    total: response.data.total,
                    totalPages: response.data.totalPages
                });
            });
        } catch (error) {
            console.error("Failed to fetch dispatch logs:", error);
            message.error("Failed to fetch dispatch logs");
        } finally {
            setLoading(false);
        }
    }

    const handleStatusChange = async (dispatched_staff_id, newStatus) => {

        try {
            let response
            if (newStatus === "Confirmed") {
                response = await api.post("/dispatch/dispatchLogs/", {
                    manual_confirm_dispatch: true,
                    dispatched_staff_id: dispatched_staff_id,
                });
            }
            else {
                response = await api.post("/dispatch/dispatchLogs/", {
                    manual_deny_dispatch: true,
                    dispatched_staff_id: dispatched_staff_id,
                });
            }
            if (response.status === 200) {
                message.success(`Staff status set to ${newStatus} successfully!`);
            } else {
                message.error('Failed to confirm the status.');
            }
        } catch {
            message.error('Failed to confirm the status.');
            console.log('Failed to confirm the status.');
        } finally {
            fetchDispatchLogs(pagination.current, pagination.pageSize, objectFilter)
            fetchCounters(objectFilter)
        }
    }

    const handleGenerateReport = async () => {
        try {

            const region = regionFilter !== 'All' ? regionFilter : undefined;

            const response = await api.get('/dispatch/storm-report/', {
                params: { region },
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `storm_report_${region || 'all'}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating report:', error);
            message.error('Failed to generate the storm report.');
        }
    };

    const handleGenerateSingleReport = async () => {
        try {
            if (stormIdFilter === 'All') {
                message.error('Please select a specific storm to generate a report.');
                return;
            }

            message.loading({ content: 'Generating storm report...', key: 'storm_report' });

            const response = await api.get('/dispatch/single-storm-report/', {
                params: { storm_id: stormIdFilter },
                responseType: 'blob',
            });

            if (response.status === 200) {
                const blob = new Blob([response.data], { type: 'application/pdf' });

                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;

                link.setAttribute('download', `storm_report_single_${stormIdFilter}.pdf`);

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                message.success({ content: `Storm report downloaded for ID: ${stormIdFilter}`, key: 'storm_report' });
            } else {
                console.error('Failed to generate storm report:', response.statusText);
                message.error('Failed to generate storm report. Please try again.');
            }
        } catch (error) {
            console.error('Error generating storm report:', error);
            message.error('An error occurred while generating the storm report.');
        }
    };

    // const getButtonStyle = (list) => ({
    //     backgroundColor: selectedObjectType === list ? '#e6f2ed' : '#fff',
    //     color: selectedObjectType === list ? '#034832' : '#000',
    //     fontWeight: '550',
    //     padding: '8px 8px',
    //     fontSize: '13px',
    //     width: '140px'
    // });


    const items = [
        {
            key: '1',
            label: 'Generate Regional Storm Report',
            icon: <IconFile size={16} />,
            onClick: handleGenerateReport,
        },
        {
            key: '2',
            label: 'Generate Single Storm Report',
            icon: <IconFile size={16} />,
            onClick: handleGenerateSingleReport,
        },
    ];

    return (
        <ConfigProvider theme={{ token: { colorLinkHover: "#034832", colorPrimary: "#034832", controlItemBgActive: "#E4F2EB" } }}>
            <DndProvider backend={HTML5Backend}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ width: 'calc(100% - 10px)', marginLeft: '20px' }}>
                        <style>
                            {`.locked-row {
                        background-color: #f0f0f0 !important;
                      }
                    `}
                        </style>
                        <style>
                            {`.ant-table-tbody > tr:hover > td {
                        background: none !important;
                      }
                  `}
                        </style>
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: '30px',
                                flexDirection: 'column'
                            }}
                        >
                            {/* Left-aligned elements: Span and New Buttons */}
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%', flexDirection: 'row', justifyContent: 'space-between', marginBottom: '30px' }}>
                                <span
                                    style={{
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        fontWeight: '600',
                                        fontSize: '40px',
                                    }}
                                >
                                    Dispatch
                                    <span style={{ fontWeight: '400' }}> - History</span>
                                </span>
                                <Dropdown menu={{ items }}>
                                    <Button className={styles.tableButtonGreen} style={{ fontSize: '14px', padding: '8px 15px', marginLeft: 8 }}>
                                        Download Reports <DownOutlined />
                                    </Button>
                                </Dropdown>
                            </div>
                        </div>

                        {/* Right-aligned elements */}
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: 15, justifyContent: 'flex-start', width: '100%' }}>
                            {/* Object Type Toggle Buttons */}
                            <Select
                                showSearch
                                placeholder="Filter by Storm"
                                style={{ width: 400, alignSelf: 'flex-end' }}
                                value={stormIdFilter !== 'All' ? stormIdFilter : undefined}
                                onChange={(value) => setStormIdFilter(value)}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                <Option key="all" value="All">
                                    All Storms
                                </Option>
                                {stormOptions.filter(storm => storm !== 'All').map(storm => (
                                    <Option key={storm.id} value={storm.id}>
                                        {`Type: ${storm.type && (storm.type).split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} - ${storm.date ? moment(storm.date).format('MMMM Do, YYYY') : 'N/A'} - ID: ${storm.id}`}
                                    </Option>
                                ))}
                            </Select>
                            {/* Walkway Crew Counters */}
                            <div style={counterBoxStyle}>
                                <h3 style={counterTitleStyle}>Walkway Crew Dispatched Staff</h3>
                                <MessageStatusCounter counts={counter.Walkway} />
                                <Typography style={metricCardSubtitle}>
                                    Total Messages: {counter.Walkway.unread + counter.Walkway.confirmed + counter.Walkway.denied}
                                </Typography>
                            </div>

                            {/* Salting Route Counters */}
                            <div style={counterBoxStyle}>
                                <h3 style={counterTitleStyle}>Salting Route Dispatched Staff</h3>
                                <MessageStatusCounter counts={counter.Salting} />
                                <Typography style={metricCardSubtitle}>
                                    Total Messages: {counter.Salting.unread + counter.Salting.confirmed + counter.Salting.denied}
                                </Typography>
                            </div>

                            {/* Loader Counters */}
                            <div style={counterBoxStyle}>
                                <h3 style={counterTitleStyle}>Loader Dispatched Staff</h3>
                                <MessageStatusCounter counts={counter.Loader} />
                                <Typography style={metricCardSubtitle}>
                                    Total Messages: {counter.Loader.unread + counter.Loader.confirmed + counter.Loader.denied}
                                </Typography>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginRight: 10, marginBottom: 20 }}>
                            <Select
                                mode="multiple"
                                size="large"
                                value={regionFilter}
                                onChange={(value) => {
                                    // If "All" is selected along with other options, remove "All"
                                    if (value.includes("All") && value.length > 1) {
                                    setRegionFilter(value.filter(v => v !== "All"));
                                    } else {
                                    setRegionFilter(value);
                                    }
                                }}
                                style={{ width: 300 }}
                                placeholder="Region"
                                >
                                {regionOptions.map((region) => (
                                    <Option key={region} value={region}>
                                    {region}
                                    </Option>
                                ))}
                                </Select>
                            <Input.Search
                                placeholder="Search vehicle"
                                value={vehicleSearch}
                                onChange={(e) => setVehicleSearch(e.target.value)}
                                style={{ width: 190 }}
                            />
                            <Input.Search
                                placeholder="Search staff"
                                value={staffSearch}
                                onChange={(e) => setStaffSearch(e.target.value)}
                                style={{ width: 190 }}
                            />
                            <Input.Search
                                placeholder="Search site"
                                value={siteSearch}
                                onChange={(e) => setSiteSearch(e.target.value)}
                                style={{ width: 190, marginRight: 'auto' }}
                            />
                            <Select
                                options={[{ value: 'Walkway', label: 'Walkway Crew\'s' }, { value: 'Salting', label: 'Salting Routes' }, { value: 'Loader', label: 'Loaders' }]}
                                value={objectFilter}
                                onChange={(value) => {
                                    setObjectFilter(value)
                                }}
                                style={{ width: 190 }}
                            />
                        </div>

                        <Spin spinning={loading}>
                            <div style={{ flex: 1 }}>
                                <Table
                                    dataSource={currentLogs}
                                    columns={objectFilter === "Walkway" ? walkwayCrewsColumns : objectFilter === 'Salting' ? saltingRoutesColumns : loadersColumns}
                                    rowKey="dispatch_log_id"
                                    defaultPageSize={50}
                                    pagination={{
                                        current: pagination.current,
                                        pageSize: pagination.pageSize,
                                        total: pagination.total,
                                        onChange: (page, pageSize) => {
                                            fetchDispatchLogs(page, pageSize, objectFilter);
                                        },
                                        showSizeChanger: true,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                    }}
                                    scroll={{ y: 690 }}
                                    expandable={{
                                        expandedRowRender: (record) => (
                                            objectFilter === "Walkway" ? walkwayCrewsExpandedRow(record) :
                                            objectFilter === 'Salting' ? saltingRoutesExpandedRow(record) :
                                                loadersExpandedRow(record)
                                        ),
                                    }}
                                />
                            </div>
                        </Spin>
                    </div>
                </div>
            </DndProvider>
        </ConfigProvider>
    );
};

export default DispatchHistoryPage;